import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Landmark, Users, AlertCircle, X, CheckCircle, Search, DollarSign,
  RefreshCw, ChevronDown, ChevronRight, ReceiptText, Wallet, Banknote,
} from 'lucide-react';
import { getReimbursementDebt, createPembayaranHutang } from '../../api';
import { useAuth } from '../../AuthContext';

// Metode pelunasan dari rekening bank perusahaan / kas.
// Transfer & Cek/Giro → kredit akun 1101 Bank, Tunai → 1100 Kas.
const METODE_PELUNASAN = [
  { key: 'Transfer', label: 'Transfer Bank', desc: 'Kredit akun 1101 Bank', icon: Landmark },
  { key: 'Cek/Giro', label: 'Cek / Giro', desc: 'Kredit akun 1101 Bank', icon: ReceiptText },
  { key: 'Tunai', label: 'Tunai (Kas)', desc: 'Kredit akun 1100 Kas', icon: Banknote },
];

export default function RekapReimbursement() {
  const { hasMenuAction } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState('');

  // Modal pelunasan
  const [pelunasan, setPelunasan] = useState(null); // { nama_petugas, notas: [...] }
  const [chosen, setChosen] = useState({});         // { 'nota_id': true }
  const [metode, setMetode] = useState('Transfer');
  const [catatan, setCatatan] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasil, setHasil] = useState(null);          // { sukses, gagal: [] }

  const load = () => {
    setLoading(true);
    getReimbursementDebt()
      .then(res => setData(res.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const rupiah = (v) => `Rp ${(Number(v) || 0).toLocaleString('id-ID')}`;

  const filtered = data.filter(d =>
    !search || String(d.nama_petugas || '').toLowerCase().includes(search.toLowerCase())
  );

  const totalDitalangi = filtered.reduce((s, d) => s + (Number(d.total) || 0), 0);
  const totalDibayar = filtered.reduce((s, d) => s + (Number(d.dibayar) || 0), 0);
  const totalSisa = filtered.reduce((s, d) => s + (Number(d.sisa) || 0), 0);
  const totalOrang = filtered.filter(d => Number(d.sisa) > 0).length;

  // --- Modal pelunasan ---
  const openPelunasan = (row) => {
    const outstanding = (row.notas || []).filter(n => Number(n.sisa) > 0);
    if (outstanding.length === 0) return;
    setError('');
    setHasil(null);
    setPelunasan({ ...row, outstanding });
    const init = {};
    outstanding.forEach(n => { init[String(n.nota_id)] = true; });
    setChosen(init);
    setMetode('Transfer');
    setCatatan(`Reimbursement untuk ${row.nama_petugas}`);
  };

  const closeModal = () => {
    if (saving) return;
    setPelunasan(null);
    setChosen({});
    setHasil(null);
  };

  const toggleNota = (id) => setChosen(c => ({ ...c, [String(id)]: !c[String(id)] }));

  const dipilih = pelunasan ? pelunasan.outstanding.filter(n => chosen[String(n.nota_id)]) : [];
  const totalBayar = dipilih.reduce((s, n) => s + (Number(n.sisa) || 0), 0);

  const jalankanPelunasan = async () => {
    if (dipilih.length === 0) { setError('Pilih minimal satu nota yang akan dilunasi'); return; }
    setSaving(true);
    setError('');
    const gagal = [];
    let sukses = 0;
    const tanggal = new Date().toISOString().slice(0, 10);
    for (const nota of dipilih) {
      try {
        await createPembayaranHutang({
          supplier_name: nota.supplier_name,
          tanggal,
          jumlah: Number(nota.sisa),
          metode_pembayaran: metode,
          catatan,
          sumber_nota: 'Pembelian',
          nota_id: nota.nota_id,
        });
        sukses += 1;
      } catch (err) {
        gagal.push(`${nota.no_nota}: ${err.response?.data?.error || 'gagal'}`);
      }
    }
    setSaving(false);
    setHasil({ sukses, gagal });
    if (sukses > 0) load();
  };

  const metodeAktif = METODE_PELUNASAN.find(m => m.key === metode) || METODE_PELUNASAN[0];

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2><Landmark size={20} style={{ marginRight: 8 }} />Rekap Utang Reimbursement Karyawan</h2>
            <p className="page-desc">
              Pembelian yang ditalangi dulu oleh petugas/teknisi dengan uang pribadi & pelunasannya dari rekening bank
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={load}>
              <RefreshCw size={16} /><span>Muat Ulang</span>
            </button>
            <button className="btn btn-primary" onClick={() => navigate('/pembayaran-hutang')}>
              <DollarSign size={16} /><span>Bayar Hutang</span>
            </button>
          </div>
        </div>
      </div>

      {/* Kartu ringkasan */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14, marginBottom: 20 }}>
        <div className="stat-card" style={{ borderLeft: '4px solid #7c3aed' }}>
          <div className="stat-card-header"><Wallet size={16} /><span>Total Ditalangi</span></div>
          <div className="stat-card-value" style={{ color: '#7c3aed', fontSize: '1.3rem' }}>{rupiah(totalDitalangi)}</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #059669' }}>
          <div className="stat-card-header"><CheckCircle size={16} /><span>Sudah Dilunasi</span></div>
          <div className="stat-card-value" style={{ color: '#059669', fontSize: '1.3rem' }}>{rupiah(totalDibayar)}</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #dc2626' }}>
          <div className="stat-card-header"><AlertCircle size={16} /><span>Sisa Belum Dibayar</span></div>
          <div className="stat-card-value" style={{ color: '#dc2626', fontSize: '1.3rem' }}>{rupiah(totalSisa)}</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #4f6ef7' }}>
          <div className="stat-card-header"><Users size={16} /><span>Karyawan Belum Lunas</span></div>
          <div className="stat-card-value" style={{ color: '#4f6ef7', fontSize: '1.3rem' }}>{totalOrang}</div>
        </div>
      </div>

      {/* Pencarian */}
      <div className="search-box" style={{ marginBottom: 14 }}>
        <Search size={18} />
        <input type="text" placeholder="Cari nama karyawan / teknisi..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? <div className="page-loading">Memuat data reimbursement...</div> : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Petugas / Teknisi</th>
                <th style={{ textAlign: 'center' }}>Nota</th>
                <th style={{ textAlign: 'right' }}>Total Ditalangi</th>
                <th style={{ textAlign: 'right' }}>Sudah Dibayar</th>
                <th style={{ textAlign: 'right' }}>Sisa</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ width: 210 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d, i) => {
                const key = `${d.nama_petugas}-${i}`;
                const lunas = Number(d.sisa) <= 0;
                const open = expanded === key;
                return (
                  <React.Fragment key={key}>
                    <tr>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <button
                            className="btn-icon"
                            title={open ? 'Tutup detail nota' : 'Lihat detail nota'}
                            onClick={() => setExpanded(open ? null : key)}
                          >
                            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                          <div>
                            <div style={{ fontWeight: 600 }}>{d.nama_petugas}</div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>
                              {d.jumlah_nota} nota ditalangi
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>{d.jumlah_nota}</td>
                      <td style={{ textAlign: 'right' }}>{rupiah(d.total)}</td>
                      <td style={{ textAlign: 'right', color: '#059669' }}>{rupiah(d.dibayar)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: lunas ? '#059669' : '#dc2626' }}>{rupiah(d.sisa)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 600,
                          background: lunas ? '#ecfdf5' : '#fef2f2', color: lunas ? '#059669' : '#dc2626',
                        }}>
                          {lunas ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                          {lunas ? 'Lunas' : 'Belum Lunas'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {hasMenuAction('pembayaran_hutang', 'create') && (
                            <button
                              className="btn btn-sm"
                              disabled={lunas}
                              title={lunas ? 'Sudah lunas' : `Lunasi semua/rekap nota ${d.nama_petugas}`}
                              style={{
                                background: lunas ? '#d1d5db' : '#4f6ef7', color: 'white', border: 'none',
                                padding: '5px 12px', borderRadius: 6, cursor: lunas ? 'not-allowed' : 'pointer',
                                fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4,
                              }}
                              onClick={() => openPelunasan(d)}
                            >
                              <Landmark size={12} /> Pelunasan
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-secondary"
                            style={{ padding: '5px 10px', fontSize: '0.75rem' }}
                            onClick={() => setExpanded(open ? null : key)}
                          >
                            <ReceiptText size={12} /> Nota
                          </button>
                        </div>
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={7} style={{ background: '#faf5ff', padding: 0 }}>
                          <div style={{ padding: '10px 14px' }}>
                            <div style={{ fontSize: '0.72rem', color: '#6d28d9', fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>
                              Nota pembelian yang ditalangi {d.nama_petugas}
                            </div>
                            <table className="data-table" style={{ background: 'white' }}>
                              <thead>
                                <tr>
                                  <th>No. Pembelian</th>
                                  <th>Tanggal</th>
                                  <th>Supplier</th>
                                  <th style={{ textAlign: 'right' }}>Total</th>
                                  <th style={{ textAlign: 'right' }}>Dibayar</th>
                                  <th style={{ textAlign: 'right' }}>Sisa</th>
                                  <th>Catatan Reimbursement</th>
                                  <th style={{ width: 90 }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {(d.notas || []).map(n => (
                                  <tr key={`${n.sumber}-${n.nota_id}`}>
                                    <td><strong style={{ fontSize: '0.78rem' }}>{n.no_nota}</strong></td>
                                    <td>{n.tanggal}</td>
                                    <td>{n.supplier_name || '-'}</td>
                                    <td style={{ textAlign: 'right' }}>{rupiah(n.total)}</td>
                                    <td style={{ textAlign: 'right', color: '#059669' }}>{rupiah(n.dibayar)}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 600, color: Number(n.sisa) > 0 ? '#dc2626' : '#059669' }}>
                                      {Number(n.sisa) > 0 ? rupiah(n.sisa) : 'Lunas'}
                                    </td>
                                    <td style={{ fontSize: '0.75rem', color: '#6d28d9' }}>{n.catatan_reimbursement || '-'}</td>
                                    <td>
                                      {Number(n.sisa) > 0 && (
                                        <button
                                          className="btn btn-sm btn-secondary"
                                          style={{ padding: '4px 8px', fontSize: '0.72rem' }}
                                          title="Buka halaman Bayar Hutang untuk nota ini"
                                          onClick={() => navigate('/pembayaran-hutang', {
                                            state: { mode: 'nota', petugas: d.nama_petugas, supplier: n.supplier_name },
                                          })}
                                        >
                                          Bayar
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted" style={{ padding: 40 }}>
                    {search ? 'Karyawan tidak ditemukan' : 'Belum ada pembelian bermetode Reimbursement'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal pelunasan */}
      {pelunasan && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 620 }}>
            <div className="modal-header">
              <h3><Landmark size={18} style={{ marginRight: 8 }} />Pelunasan Reimbursement — {pelunasan.nama_petugas}</h3>
              <button className="btn-icon" onClick={closeModal}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}

              {hasil ? (
                <div style={{
                  padding: '12px 14px', borderRadius: 8, marginBottom: 12,
                  background: hasil.gagal.length ? '#fffbeb' : '#ecfdf5',
                  border: `1px solid ${hasil.gagal.length ? '#fde68a' : '#a7f3d0'}`,
                }}>
                  <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, color: hasil.gagal.length ? '#b45309' : '#059669' }}>
                    <CheckCircle size={15} />
                    {hasil.sukses} nota berhasil dilunasi{hasil.gagal.length ? `, ${hasil.gagal.length} gagal` : ''}
                  </div>
                  {hasil.gagal.length > 0 && (
                    <ul style={{ margin: '8px 0 0 18px', fontSize: '0.78rem', color: '#b45309' }}>
                      {hasil.gagal.map((g, i) => <li key={i}>{g}</li>)}
                    </ul>
                  )}
                </div>
              ) : (
                <>
                  <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginBottom: 10 }}>
                    Pilih nota yang akan dilunasi dari rekening bank perusahaan. Satu nota = satu jurnal
                    <strong> D 2211 Utang Reimbursement Karyawan</strong>.
                  </div>

                  <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
                    {pelunasan.outstanding.map(n => (
                      <label key={n.nota_id} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                        borderBottom: '1px solid var(--gray-100)', cursor: 'pointer', background: chosen[String(n.nota_id)] ? '#f5f3ff' : 'white',
                      }}>
                        <input type="checkbox" checked={!!chosen[String(n.nota_id)]} onChange={() => toggleNota(n.nota_id)} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{n.no_nota}</div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>
                            {n.supplier_name || '-'} · {n.tanggal}{n.catatan_reimbursement ? ` · ${n.catatan_reimbursement}` : ''}
                          </div>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#dc2626' }}>{rupiah(n.sisa)}</div>
                      </label>
                    ))}
                  </div>

                  <div className="form-group">
                    <label>Bayar dari</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {METODE_PELUNASAN.map(m => {
                        const Icon = m.icon;
                        const active = metode === m.key;
                        return (
                          <button key={m.key} type="button" onClick={() => setMetode(m.key)} style={{
                            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                            padding: '9px 6px', borderRadius: 8, cursor: 'pointer',
                            border: `1px solid ${active ? '#16a34a' : 'var(--gray-200)'}`,
                            background: active ? '#16a34a' : '#fff',
                            color: active ? '#fff' : 'var(--gray-500)',
                          }}>
                            <Icon size={15} />
                            <span style={{ fontSize: '0.76rem', fontWeight: 600 }}>{m.label}</span>
                            <span style={{ fontSize: '0.66rem', opacity: 0.85 }}>{m.desc}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Catatan (opsional)</label>
                    <input type="text" value={catatan} onChange={e => setCatatan(e.target.value)}
                      placeholder={`Reimbursement untuk ${pelunasan.nama_petugas}`} />
                  </div>

                  <div style={{
                    marginTop: 12, padding: '12px 16px', borderRadius: 8,
                    background: '#f8fafc', border: '1px solid #e2e8f0',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: '0.82rem', color: '#475569' }}>Nota dipilih</span>
                      <span style={{ fontWeight: 600, fontSize: '0.82rem' }}>{dipilih.length} nota</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: '0.82rem', color: '#475569' }}>Metode</span>
                      <span style={{ fontWeight: 500, fontSize: '0.82rem' }}>{metodeAktif.label} · {metodeAktif.desc}</span>
                    </div>
                    <div style={{ borderTop: '1px solid #e2e8f0', margin: '6px 0', paddingTop: 6, display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>Total dibayar</span>
                      <span style={{ fontWeight: 700, fontSize: '1rem', color: '#16a34a' }}>{rupiah(totalBayar)}</span>
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 6 }}>
                      Jurnal otomatis per nota: <strong>D 2211 Utang Reimbursement Karyawan</strong> / K {metode === 'Tunai' ? '1100 Kas' : '1101 Bank'}
                      {catatan ? <> · keterangan pembayaran: “{catatan}”</> : null}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              {hasil ? (
                <button className="btn btn-secondary" onClick={() => { setPelunasan(null); setHasil(null); }}>Tutup</button>
              ) : (
                <>
                  <button className="btn btn-secondary" onClick={closeModal}>Batal</button>
                  <button
                    className="btn btn-primary"
                    onClick={jalankanPelunasan}
                    disabled={saving || dipilih.length === 0}
                    style={{ background: '#4f6ef7', borderColor: '#4f6ef7' }}
                  >
                    {saving
                      ? 'Memproses...'
                      : <><Landmark size={16} /> Lunasi {dipilih.length} nota ({rupiah(totalBayar)})</>}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
